<template>
	<v-container>
		<!-- Search and Suggestions Section -->
		<v-row class="contentRow">
			<v-col>
				<h1 class="mb-2">Search Connections</h1>
				<p class="mb-10 text-center">
					Here you can search for companies to connect with. You can search by
					name or client ID.
				</p>
			</v-col>
		</v-row>
		<v-form ref="form" v-model="formIsValid">
			<v-row class="contentRow">
				<v-col cols="12">
					<h2 class="text-start">Search Client</h2>
				</v-col>
				<v-col cols="12" md="8">
					<v-text-field
						label="Enter client name or ID"
						:rules="notEmptyRule"
						v-model="searchQuery"
						@input="onSearchInput"
						filled
						dense
						hide-details="auto"
						class="search-field"
					></v-text-field>
					<v-card
						v-if="showSuggestions"
						class="suggestion-list mt-1 elevation-5 rounded-lg"
					>
						<v-list class="scrollable-list">
							<v-list-item-group>
								<v-list-item
									v-for="client in filteredClients"
									:key="client.ClientID"
									@click="selectClient(client)"
									class="suggestion-item"
								>
									<v-list-item-avatar class="mr-3 suggestion-avatar">
										<v-avatar color="primary" size="40">
											<!-- Use CompanyLogo if available, otherwise use the first letter of FullName -->
											<v-img
												v-if="client.CompanyLogo"
												:src="client.CompanyLogo"
												alt="Company Logo"
											></v-img>
											<span v-else class="white--text">{{ client.FullName.charAt(0) }}</span>
										</v-avatar>
									</v-list-item-avatar>
									<v-list-item-content>
										<v-list-item-title class="font-weight-medium">{{ client.FullName }}</v-list-item-title>
										<v-list-item-subtitle
											>ID: {{ client.ClientID }} |
											{{ client.Email }}</v-list-item-subtitle
										>
									</v-list-item-content>
									<v-list-item-action>
										<v-icon color="primary">mdi-plus-circle</v-icon>
									</v-list-item-action>
								</v-list-item>
								<v-list-item v-if="filteredClients.length === 0">
									<v-list-item-content>
										<v-list-item-title class="text-center"
											>No results found</v-list-item-title
										>
									</v-list-item-content>
								</v-list-item>
							</v-list-item-group>
						</v-list>
					</v-card>
				</v-col>
			</v-row>
		</v-form>

		<!-- Data Table Section -->
		<v-row class="contentRow">
			<v-col cols="12">
				<v-data-table
					:headers="tableHeaders"
					:items="selectedClients"
					hide-default-footer
				>
					<!-- New column for the avatar -->
					<template v-slot:item.avatar="{ item }">
						<v-avatar color="primary" size="40">
							<v-img
								v-if="item.CompanyLogo"
								:src="item.CompanyLogo"
								alt="Company Logo"
							></v-img>
							<span v-else class="white--text">{{ item.FullName.charAt(0) }}</span>
						</v-avatar>
					</template>
					<template v-slot:item.actions="{ item }">
						<v-btn
							color="primary"
							@click="addConnection(item)"
							:disabled="item.Pending"
						>
							{{ item.Pending ? "Requested" : "Add Connection" }}
						</v-btn>
					</template>
				</v-data-table>
			</v-col>
		</v-row>

		<!-- Success Dialog -->
		<v-dialog v-model="showSuccessDialog" max-width="400">
			<v-card>
				<v-card-title class="headline">Connection Request Sent!</v-card-title>
				<v-card-text>
					See the Requests tab to view all your connection requests.
				</v-card-text>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn color="primary" @click="showSuccessDialog = false">OK</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-container>
</template>

<script>
import ConnectionAPI from "../../api/connections/connection"
import LoadingState from "../../consts/LoadingState"
export default {
	name: "ConnectionRequests",
	data() {
		return {
			formIsValid: false,
			searchQuery: "",
			showSuggestions: false,
			apiClients: [], // Store the API data here
			selectedClients: [], // Corrected typo: selectedClients
			hasFetchedData: false, // Track if data has been fetched
			showSuccessDialog: false, // Control the success dialog visibility
			tableHeaders: [
				{ text: "", value: "avatar", sortable: false,width:"5%", }, // New column for the avatar
				{ text: "Client Name", value: "FullName", sortable: false },
				{ text: "Client ID", value: "ClientID", sortable: false },
				{ text: "Email", value: "Email", sortable: false },
				{ text: "Actions", value: "actions", sortable: false },
			],
		}
	},
	computed: {
		filteredClients() {
			if (this.searchQuery.length < 1) {
				return []
			}

			const query = this.searchQuery.toLowerCase()

			return this.apiClients.filter((client) =>
				client.FullName.toLowerCase().includes(query) ||
				String(client.ClientID).includes(query)
			)
		}
	},
	methods: {
		loadItems() {
			if (this.hasFetchedData) {
				return // Do not fetch again if data is already fetched
			}

			this.$store.dispatch(
				"loading/" + LoadingState.ACTION_START_LOADING,
				null,
				{ root: true }
			)
			ConnectionAPI.getAllConnections(this.searchQuery)
				.then((data) => {
					this.apiClients = data // Store the API data
					this.hasFetchedData = true // Mark data as fetched
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_END_LOADING,
						null,
						{ root: true }
					)
				})
				.catch((error) => {
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_SET_ERROR,
						error,
						{ root: true }
					)
				})
		},
		onSearchInput() {
			if (this.searchQuery.length >= 1) {
				this.showSuggestions = true
				this.loadItems() // Fetch data only if not already fetched
			} else {
				this.showSuggestions = false
				this.hasFetchedData = false // Reset fetch flag if search query is cleared
			}
		},
		selectClient(client) {
			if (!this.selectedClients.some((c) => c.ClientID === client.ClientID)) {
				this.selectedClients.push(client)
			}
			this.searchQuery = ""
			this.showSuggestions = false
		},
		addConnection(client) {
			// Call the API to add the connection
			ConnectionAPI.addConnection(client.ClientID)
				.then(() => {
					// Update the client's Pending status
					client.Pending = true

					// Show the success dialog
					this.showSuccessDialog = true

					// Notify the user
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_LOADING_SUCCESS,
						"Connection request was sent!",
						{ root: true }
					)
				})
				.catch((reason) => {
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_SET_ERROR,
						reason,
						{ root: true }
					)
				})
		},
		resetComponent() {
			// Reset all relevant data properties
			this.searchQuery = ""
			this.showSuggestions = false
			this.apiClients = []
			this.selectedClients = []
			this.hasFetchedData = false
			this.showSuccessDialog = false

			// Reload data
			//this.loadItems()
		},
	},
	activated() {
		// This hook is called when the component is activated (e.g., when navigating back to it)
		this.resetComponent()
	},
	mounted() {
		// This hook is called when the component is first mounted
		this.resetComponent()
	},
}
</script>

<style scoped>
/* Add your styles here */

/* Make the suggestion list scrollable */
.scrollable-list {
	max-height: 250px; /* Set a fixed height for the list */
	overflow-y: auto; /* Enable vertical scrolling */
}

/* Optional: Style the scrollbar */
.scrollable-list::-webkit-scrollbar {
	width: 8px; /* Width of the scrollbar */
}

.scrollable-list::-webkit-scrollbar-track {
	background: #f1f1f1; /* Color of the track */
	border-radius: 4px;
}

.scrollable-list::-webkit-scrollbar-thumb {
	background: #888; /* Color of the scrollbar */
	border-radius: 4px;
}

.scrollable-list::-webkit-scrollbar-thumb:hover {
	background: #555; /* Color of the scrollbar on hover */
}
</style>
